import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Paper,
  Button,
  Divider,
  Switch,
  FormControlLabel,
  useTheme,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  alpha,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import {
  Check as CheckIcon,
  Close as CloseIcon,
  ArrowForward as ArrowForwardIcon,
  StarOutline as StarOutlineIcon,
  Storefront as BusinessIcon,
  People as TeamIcon,
  ExpandMore as ExpandMoreIcon
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';

const Pricing: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [annually, setAnnually] = useState(true);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };

  const pricingPlans = [
    {
      title: 'Basic',
      subtitle: 'Invoicing, Client Management, Cashbook',
      monthlyPrice: 499,
      annualPrice: 4990,
      highlight: false,
      icon: <BusinessIcon fontSize="large" />,
      color: theme.palette.primary.main,
      features: [
        'Up to 100 transactions per month',
        'Basic invoice management',
        'Income & expense tracking',
        'GST ready reports',
        'Single user access',
        'Email support',
      ],
      notIncluded: [
        'Inventory management',
        'Multi-user access',
        'Custom branding',
        'Priority support',
        'Advanced analytics',
      ]
    },
    {
      title: 'Pro',
      subtitle: 'Basic + Smart POS, Inventory, GST Automation',
      monthlyPrice: 999,
      annualPrice: 9990,
      highlight: true,
      icon: <StarOutlineIcon fontSize="large" />,
      color: theme.palette.secondary.main,
      features: [
        'Up to 500 transactions per month',
        'Advanced invoice management',
        'Income & expense tracking',
        'GST ready reports',
        'Inventory management',
        'Up to 3 user accounts',
        'Priority email support',
        'Custom branding',
      ],
      notIncluded: [
        'Advanced analytics',
        'API access',
      ]
    },
    {
      title: 'Enterprise',
      subtitle: 'Pro + Auto-Settlement Payroll, Staff Advances, RBAC',
      monthlyPrice: 1999,
      annualPrice: 19990,
      highlight: false,
      icon: <TeamIcon fontSize="large" />,
      color: '#6A1B9A',
      features: [
        'Unlimited transactions',
        'Advanced invoice management',
        'Income & expense tracking',
        'GST ready reports',
        'Inventory management',
        'Unlimited user accounts',
        'Priority phone & email support',
        'Custom branding',
        'Advanced analytics',
        'API access',
      ],
      notIncluded: []
    }
  ];

  const toggleBillingCycle = () => {
    setAnnually(!annually);
  };

  return (
    <>
      <Box sx={{ overflow: 'hidden', pt: { xs: 10, md: 12 }, pb: { xs: 8, md: 10 } }}>
        <Container maxWidth="lg">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Box textAlign="center" mb={6}>
              <Typography
                variant="h2"
                fontWeight="bold"
                gutterBottom
                sx={{ fontSize: { xs: '2.5rem', md: '3.5rem' } }}
              >
                Simple, Transparent{' '}
                <Box
                  component="span"
                  sx={{
                    backgroundImage: 'var(--primary-gradient)',
                    backgroundClip: 'text',
                    color: 'transparent',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                  }}
                >
                  Pricing
                </Box>
              </Typography>
              <Typography
                variant="h6"
                color="text.secondary"
                sx={{ maxWidth: 700, mx: 'auto', mb: 5 }}
              >
                Choose the perfect plan for your business needs. Start with a 14-day free trial.
              </Typography>

              <Box display="flex" justifyContent="center" alignItems="center" mb={6}>
                <Typography variant="body1" color={annually ? 'text.secondary' : 'primary'}>Monthly</Typography>
                <FormControlLabel
                  control={
                    <Switch
                      checked={annually}
                      onChange={toggleBillingCycle}
                      color="primary"
                      sx={{ mx: 1 }}
                    />
                  }
                  label=""
                />
                <Box display="flex" alignItems="center">
                  <Typography variant="body1" color={annually ? 'primary' : 'text.secondary'}>Annual</Typography>
                  <Chip
                    label="2 Months Free"
                    size="small"
                    color="primary"
                    sx={{ ml: 1, display: annually ? 'flex' : 'none', fontWeight: 'bold' }}
                  />
                </Box>
              </Box>
            </Box>
          </motion.div>

          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            <Grid container spacing={3} justifyContent="center">
              {pricingPlans.map((plan, index) => (
                <Grid item xs={12} md={4} key={index}>
                  <motion.div variants={itemVariants} custom={index} style={{ height: '100%' }}>
                    <Paper
                      elevation={plan.highlight ? 8 : 2}
                      sx={{
                        p: 4,
                        height: '100%',
                        borderRadius: 4,
                        position: 'relative',
                        overflow: 'hidden',
                        transition: 'all 0.3s ease',
                        transform: plan.highlight ? 'scale(1.05)' : 'scale(1)',
                        border: plan.highlight ? `2px solid ${plan.color}` : 'none',
                        '&:hover': {
                          transform: plan.highlight ? 'scale(1.05)' : 'scale(1.02)',
                          boxShadow: plan.highlight ? 8 : 4,
                        },
                      }}
                    >
                      {plan.highlight && (
                        <Chip
                          label="Most Popular"
                          color="secondary"
                          size="small"
                          sx={{
                            position: 'absolute',
                            top: 16,
                            right: 16,
                            fontWeight: 'bold',
                          }}
                        />
                      )}

                      <Box
                        sx={{
                          mb: 3,
                          p: 1,
                          display: 'inline-flex',
                          borderRadius: '50%',
                          backgroundColor: alpha(plan.color, 0.1),
                          color: plan.color,
                        }}
                      >
                        {plan.icon}
                      </Box>

                      <Typography variant="h4" fontWeight="bold" sx={{ mb: 1 }}>
                        {plan.title}
                      </Typography>
                      <Typography variant="body1" color="text.secondary" sx={{ mb: 3, height: 48 }}>
                        {plan.subtitle}
                      </Typography>

                      <Box display="flex" alignItems="baseline" mb={3} sx={{ flexWrap: 'wrap' }}>
                        {annually && (
                          <Typography variant="h5" color="text.disabled" sx={{ textDecoration: 'line-through', mr: 1 }}>
                            ₹{plan.monthlyPrice.toLocaleString()}/mo
                          </Typography>
                        )}
                        <Typography variant="h3" fontWeight="bold" color={plan.highlight ? 'secondary.main' : 'primary.main'}>
                          ₹{annually ? plan.annualPrice.toLocaleString() : plan.monthlyPrice.toLocaleString()}
                        </Typography>
                        <Typography variant="body1" color="text.secondary" sx={{ ml: 1 }}>
                          {annually ? '/year' : '/month'}
                        </Typography>
                      </Box>

                      <Button
                        variant={plan.highlight ? 'contained' : 'outlined'}
                        color={plan.highlight ? 'secondary' : 'primary'}
                        fullWidth
                        size="large"
                        endIcon={<ArrowForwardIcon />}
                        onClick={() => navigate('/signup')}
                        sx={{
                          py: 1.5,
                          borderRadius: '50px',
                          mb: 3,
                          ...(plan.highlight && {
                            backgroundImage: 'var(--primary-gradient)',
                            '&:hover': {
                              boxShadow: `0 8px 25px ${alpha(plan.color, 0.3)}`,
                            },
                          }),
                        }}
                      >
                        Get Started
                      </Button>

                      <Divider sx={{ my: 3 }} />

                      <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                        What's included:
                      </Typography>

                      <List disablePadding>
                        {plan.features.map((feature, i) => (
                          <ListItem key={i} disableGutters sx={{ py: 1 }}>
                            <ListItemIcon sx={{ minWidth: 32 }}>
                              <CheckIcon color="success" fontSize="small" />
                            </ListItemIcon>
                            <ListItemText primary={feature} primaryTypographyProps={{ variant: 'body2' }} />
                          </ListItem>
                        ))}

                        {plan.notIncluded.length > 0 && (
                          <>
                            <Box mt={2} mb={1}>
                              <Typography variant="body2" color="text.secondary">
                                Not included:
                              </Typography>
                            </Box>
                            {plan.notIncluded.map((feature, i) => (
                              <ListItem key={i} disableGutters sx={{ py: 0.5 }}>
                                <ListItemIcon sx={{ minWidth: 32 }}>
                                  <CloseIcon color="error" fontSize="small" />
                                </ListItemIcon>
                                <ListItemText 
                                  primary={feature} 
                                  primaryTypographyProps={{ 
                                    variant: 'body2', 
                                    color: 'text.secondary' 
                                  }} 
                                />
                              </ListItem>
                            ))}
                          </>
                        )}
                      </List>
                    </Paper>
                  </motion.div>
                </Grid>
              ))}
            </Grid>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
          >
            <Box 
              mt={10} 
              p={5} 
              borderRadius={4} 
              sx={{ 
                background: 'linear-gradient(135deg, rgba(71, 118, 230, 0.1) 0%, rgba(142, 84, 233, 0.1) 100%)',
                textAlign: 'center' 
              }}
            >
              <Typography variant="h5" fontWeight="bold" gutterBottom>
                Need a custom plan for your enterprise?
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 700, mx: 'auto', mb: 3 }}>
                We offer tailored solutions for large businesses with specific requirements.
                Contact our sales team to discuss your needs.
              </Typography>
              <Button
                variant="contained"
                size="large"
                onClick={() => navigate('/contact')}
                sx={{
                  px: 4,
                  py: 1.5,
                  borderRadius: '50px',
                  backgroundImage: 'var(--primary-gradient)',
                  '&:hover': {
                    boxShadow: '0 8px 25px rgba(71, 118, 230, 0.3)',
                  },
                }}
              >
                Contact Sales
              </Button>
            </Box>
          </motion.div>

          {/* FAQ Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.6 }}
          >
            <Box mt={10}>
              <Typography variant="h4" fontWeight="bold" textAlign="center" mb={6}>
                Frequently Asked Questions
              </Typography>
              
              <Box sx={{ maxWidth: 800, mx: 'auto' }}>
                <Accordion sx={{ mb: 2, borderRadius: '8px !important', '&:before': { display: 'none' }, boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ fontWeight: 'bold' }}>
                    <Typography variant="subtitle1" fontWeight="bold">Can I cancel anytime?</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography color="text.secondary">
                      Yes, you can upgrade, downgrade, or cancel your plan at any time. If you cancel, you'll retain access to your plan until the end of your billing cycle.
                    </Typography>
                  </AccordionDetails>
                </Accordion>
                
                <Accordion sx={{ mb: 2, borderRadius: '8px !important', '&:before': { display: 'none' }, boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ fontWeight: 'bold' }}>
                    <Typography variant="subtitle1" fontWeight="bold">How secure is my data on Akmo Books?</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography color="text.secondary">
                      We take security seriously. Akmo Books is built entirely on AWS infrastructure utilizing Amazon Cognito for enterprise-grade authentication and strict Role-Based Access Control (RBAC). Your financial data is cryptographically secured.
                    </Typography>
                  </AccordionDetails>
                </Accordion>
                
                <Accordion sx={{ borderRadius: '8px !important', '&:before': { display: 'none' }, boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ fontWeight: 'bold' }}>
                    <Typography variant="subtitle1" fontWeight="bold">Do you help with data migration from my old software?</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography color="text.secondary">
                      Absolutely! We offer free onboarding assistance, including a guided import process for your existing ledgers, inventory lists, and customer data using our simple CSV mapping tool.
                    </Typography>
                  </AccordionDetails>
                </Accordion>
              </Box>
            </Box>
          </motion.div>
        </Container>
      </Box>
    </>
  );
};

export default Pricing;